import React, { useState, useRef, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import { injectIntl } from "react-intl";
import { inject, observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { 
  DeviceSwipeContainer,
  DeviceSlider,
  DeviceSlide,
  DeviceCard,
  ProfileSetupButton,
  ProfileGuideBubble,
  AddDeviceCard,
  AddDeviceIcon,
  SelectedBadge,
  FireworkContainer,
  PaginationContainer,
  PaginationDot
} from "../Styled/HomeStyled";
import { Typography, Stack } from "@mui/material";
import { c, getColor } from "../../../theme/colorUtils";

const PokoroListSlide = (props) => {
  const { 
    intl, 
    authStore, 
    userStore, 
    modalStore,
    getDeviceDisplayName,
    onDeviceSelect,
    onAddDevice
  } = props;
  
  const history = useHistory();
  const sliderRef = useRef(null);
  
  const devices = userStore.userDeviceList || [];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTracking, setIsTracking] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [containerWidth, setContainerWidth] = useState(320);
  // const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [previousSelectedDeviceId, setPreviousSelectedDeviceId] = useState(null);
  const [fireworkTrigger, setFireworkTrigger] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [touchDisabled, setTouchDisabled] = useState(false);
  const [showConnectLoading, setShowConnectLoading] = useState(false);

  const selectedDeviceId = userStore.selectedDevice?.deviceId;
  const getProfileBirthYear = useCallback((profile) => {
    if (!profile) return "";

    const rawBirthDate = profile.birthDate ?? profile.birth;
    if (!rawBirthDate) return "";

    const yearMatch = String(rawBirthDate).match(/\d{4}/);
    return yearMatch ? yearMatch[0] : "";
  }, []);

  const getProfileGenderLabel = useCallback((profile) => {
    if (!profile?.gender) {
      return intl.formatMessage({ id: "device_detail_gender_unknown" });
    }

    const normalizedGender = String(profile.gender).toUpperCase();
    if (normalizedGender === "M" || normalizedGender === "MALE" || normalizedGender === "BOY") {
      return intl.formatMessage({ id: "device_detail_gender_boy" });
    }
    if (normalizedGender === "F" || normalizedGender === "FEMALE" || normalizedGender === "GIRL") {
      return intl.formatMessage({ id: "device_detail_gender_girl" });
    }
    return intl.formatMessage({ id: "device_detail_gender_unknown" });
  }, [intl]);

  const getProfileDescription = useCallback((profile) => {
    if (!profile) return "";

    const profileBirthYear = getProfileBirthYear(profile);
    const profileGenderLabel = getProfileGenderLabel(profile);

    if (!profileBirthYear && !profileGenderLabel) {
      return "";
    }
    if (!profileBirthYear) {
      return profileGenderLabel;
    }
    if (!profileGenderLabel) {
      return intl.formatMessage({ id: "device_detail_birth_year" }, { year: profileBirthYear });
    }

    return `${intl.formatMessage({ id: "device_detail_birth_year" }, { year: profileBirthYear })} / ${profileGenderLabel}`;
  }, [getProfileBirthYear, getProfileGenderLabel, intl]);

  const handleAddDevice = () => {
    history.push('/pokoroConnect?hideSkipButton=true');
  };



  // 새 기기 등록 카드를 포함한 전체 슬라이드 수
  const allSlides = [...devices.map(device => ({
    ...device,
    id: device.deviceId || device.id
  })), { id: 'add-device', isAddCard: true }];
  const totalSlides = allSlides.length;

  // 스토어에 선택된 기기가 없을 때만 목록의 첫 번째로 기본 선택
  // (목록 재조회 전 캐시된 userDeviceList로는 선택하지 않음 — isUserDeviceListLoading 가드)
  useEffect(() => {
    if (devices.length === 0) return;
    if (userStore.isUserDeviceListLoading) return;
    if (userStore.selectedDevice?.deviceId) return;

    const firstDevice = devices[0];
    userStore.setSelectedDevice(firstDevice);
    if (onDeviceSelect) {
      onDeviceSelect({
        ...firstDevice,
        isSelected: true
      });
    }
  }, [devices, onDeviceSelect, userStore.selectedDevice?.deviceId, userStore.isUserDeviceListLoading]);

  // 특정 기기로 슬라이드 이동 (devices 로드·selectedDevice 변경 시 동기화)
  const moveToDeviceSlide = useCallback((deviceId) => {
    const slides = [
      ...devices.map((device) => ({
        ...device,
        id: device.deviceId || device.id
      })),
      { id: "add-device", isAddCard: true }
    ];
    const deviceIndex = slides.findIndex((slide) => slide.id === deviceId);
    if (deviceIndex !== -1) {
      console.log("[PokoroListSlide] Moving to device slide:", {
        deviceId,
        deviceIndex
      });
      setIsTracking(false);
      setCurrentSlide(deviceIndex);
    }
  }, [devices]);

  useEffect(() => {
    if (devices.length === 0 || !selectedDeviceId) return;
    moveToDeviceSlide(selectedDeviceId);
  }, [devices, selectedDeviceId, moveToDeviceSlide]);

  // 모달 상태 변화 감시 및 로컬 상태 동기화
  useEffect(() => {
    const modalOpen = modalStore.changePokoroModalOpen;
    const wasModalOpen = isModalOpen;

    setIsModalOpen(modalOpen);

    console.log('[PokoroListSlide] Modal state changed:', {
      modalOpen,
      wasModalOpen,
      previousSelectedDeviceId,
      currentNewSelectedDevice: userStore.newSelectedDevice
    });

    // 모달이 닫혔고 (이전에 열려있었고), 이전 선택 상태가 있는 경우
    if (wasModalOpen && !modalOpen && previousSelectedDeviceId !== null) {
      // 모달이 닫힌 후 잠시 터치 이벤트 비활성화
      setTouchDisabled(true);

      // 약간의 지연을 두어 스토어 업데이트가 완료되도록 함
      setTimeout(() => {
        const currentUserDevice = userStore.newSelectedDevice;

        console.log('[PokoroListSlide] Checking for restoration:', {
          currentUserDevice,
          isEmptyString: currentUserDevice === '',
          isFalsy: !currentUserDevice
        });

        // 만약 userStore.newSelectedDevice가 빈 문자열이거나 falsy라면 (취소된 경우)
        if (!currentUserDevice || currentUserDevice === '') {
          console.log('[PokoroListSlide] Restoring to previous device:', previousSelectedDeviceId);

          // 이전 선택 상태로 복원
          userStore.setSelectedDeviceById(previousSelectedDeviceId);
          // setSelectedDeviceId(previousSelectedDeviceId);
          
          // 복원된 기기로 슬라이드 이동 (약간의 지연으로 부드러운 효과)
          setTimeout(() => {
            moveToDeviceSlide(previousSelectedDeviceId);
          }, 100);
          
          // 이전 기기 정보 찾기
          const previousDevice = devices.find(device => device.id === previousSelectedDeviceId);
          if (previousDevice && onDeviceSelect) {
            onDeviceSelect({
              ...previousDevice,
              isSelected: true
            });
          }
        }
        
        // 이전 상태 초기화
        setPreviousSelectedDeviceId(null);
        
        // 터치 이벤트 재활성화
        setTimeout(() => {
          setTouchDisabled(false);
        }, 300); // 추가 300ms 대기
      }, 200); // 200ms 지연으로 증가
    }
  }, [modalStore.changePokoroModalOpen, isModalOpen, previousSelectedDeviceId, devices, onDeviceSelect, moveToDeviceSlide]);

  // 화면 너비에 따른 동적 계산
  const visibleSlides = 1; // 한 번에 보이는 슬라이드 수 (카드 100% 너비 유지)
  const padding = 32; // 좌우 패딩 (16px * 2)
  const baseSlideWidth = (containerWidth - padding) / visibleSlides; // 패딩을 제외한 기본 슬라이드 너비
  // 디바이스 카드 슬라이드 너비 (다음 슬라이드가 살짝 보이도록)
  const slideWidth = devices.length > 0 ? baseSlideWidth * 0.88 : baseSlideWidth;

  // 터치/마우스 이벤트 핸들러
  const handleStart = (clientX) => {
    setIsTracking(true);
    setStartX(clientX);
    setCurrentX(clientX);
  };

  const handleMove = (clientX) => {
    if (!isTracking) return;
    setCurrentX(clientX);
    
    // 드래그 거리 계산
    const deltaX = Math.abs(clientX - startX);
    // 5px 이상 움직이면 드래그로 간주
    if (deltaX > 5 && !isDragging) {
      setIsDragging(true);
    }
  };

  const handleEnd = () => {
    if (!isTracking) return;
    setIsTracking(false);
    
    const deltaX = currentX - startX;
    const threshold = 50; // 스와이프 임계값
    
    if (deltaX > threshold && currentSlide > 0) {
      // 왼쪽으로 스와이프 (이전 슬라이드)
      setCurrentSlide(prev => Math.max(0, prev - 1));
    } else if (deltaX < -threshold && currentSlide < totalSlides - visibleSlides) {
      // 오른쪽으로 스와이프 (다음 슬라이드)
      setCurrentSlide(prev => Math.min(totalSlides - visibleSlides, prev + 1));
    }
    
    // 짧은 지연 후 드래그 상태 초기화 (클릭 이벤트가 발생하지 않도록)
    setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };

  // 마우스 이벤트
  const handleMouseDown = (e) => {
    // 모달이 열려있으면 마우스 이벤트 무시
    if (isModalOpen) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    handleStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    // 모달이 열려있으면 마우스 이벤트 무시
    if (isModalOpen) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    handleMove(e.clientX);
  };

  const handleMouseUp = (e) => {
    // 모달이 열려있으면 마우스 이벤트 무시
    if (isModalOpen) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    handleEnd();
  };

  // 터치 이벤트
  const handleTouchStart = (e) => {
    // 모달이 열려있거나 터치가 비활성화되어 있으면 터치 이벤트 무시
    if (isModalOpen || touchDisabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    // 모달이 열려있거나 터치가 비활성화되어 있으면 터치 이벤트 무시
    if (isModalOpen || touchDisabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    // 모달이 열려있거나 터치가 비활성화되어 있으면 터치 이벤트 무시
    if (isModalOpen || touchDisabled) {
      return;
    }
    handleEnd();
  };

  // 통합된 기기 클릭 핸들러
  const handleSelectedBadgeClick = (e, device) => {
    e.stopPropagation();

    if (selectedDeviceId === device.id) return;
    if (!device.profile) return;

    // 드래그 중이면 클릭 이벤트 무시
    if (isDragging) {
      return;
    }
    
    const deviceId = device.deviceId || device.id;
    const currentSelected = selectedDeviceId;
    
    // 같은 기기를 다시 클릭한 경우 선택 해제
    if (currentSelected === deviceId) {
      // setSelectedDeviceId(null);
      // if (onDeviceSelect) {
      //   onDeviceSelect({
      //     ...device,
      //     isSelected: false
      //   });
      // }
      return;
    }
    
    // 새로운 기기를 선택한 경우
    // 이전 선택 상태 저장
    setPreviousSelectedDeviceId(currentSelected);
    
    // 새 기기로 선택 상태 변경
    userStore.setSelectedDevice(device);
    // setSelectedDeviceId(deviceId);

    // 불꽃놀이 애니메이션 트리거
    setFireworkTrigger(prev => ({
      ...prev,
      [deviceId]: Date.now()
    }));
    
    // 애니메이션 후 트리거 상태 리셋
    setTimeout(() => {
      setFireworkTrigger(prev => ({
        ...prev,
        [deviceId]: null
      }));
    }, 700);
    
    // 스토어에 선택된 기기 설정 및 모달 열기
    userStore.setNewSelectedDevice(device);
    modalStore.setChangePokoroModalOpen(true);
    
    // 부모 컴포넌트에 선택된 기기 정보 전달
    if (onDeviceSelect) {
      onDeviceSelect({
        ...device,
        isSelected: true
      });
    }
  };

  const handleDeviceClick = (device) => {
    history.push("/deviceDetail", {
      deviceId: device.deviceId,
      from: "/home",
    });
  }

  // 새 기기 등록 버튼 클릭 핸들러
  const handleAddDeviceClick = async () => {
    if (isDragging) {
      return;
    }
    const userId = authStore.loginUser?.id;
    if (!userId) {
      handleAddDevice();
      return;
    }
    try {
      const hasUnlinkedProfile = await userStore.getUnlinkedDeviceProfileExists(userId);
      if (hasUnlinkedProfile) {
        handleAddDevice();
      } else {
        authStore.initProfileInfo();
        history.push("/signin/nickname", { notNewUser: true, redirectUrl: '/pokoroConnect?hideSkipButton=true' });
      }
    } catch (e) {
      console.error("[PokoroListSlide] getUnlinkedDeviceProfileExists failed:", e);
      handleAddDevice();
    }
  };

  // 팝업 닫기 핸들러
  const handleCloseConnectLoading = () => {
    setShowConnectLoading(false);
  };

  // 최대 슬라이드 수 계산
  const maxSlides = Math.max(0, totalSlides - Math.floor(visibleSlides));

  // 컨테이너 크기 측정
  useEffect(() => {
    const updateContainerWidth = () => {
      if (sliderRef.current?.parentElement) {
        const width = sliderRef.current.parentElement.offsetWidth;
        setContainerWidth(width);
      }
    };

    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);

    return () => {
      window.removeEventListener('resize', updateContainerWidth);
    };
  }, []);

  // 마우스 이벤트 리스너
  useEffect(() => {
    const handleMouseMoveGlobal = (e) => {
      if (isTracking) handleMouseMove(e);
    };

    const handleMouseUpGlobal = (e) => {
      if (isTracking) handleMouseUp(e);
    };

    if (isTracking) {
      document.addEventListener('mousemove', handleMouseMoveGlobal);
      document.addEventListener('mouseup', handleMouseUpGlobal);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMoveGlobal);
      document.removeEventListener('mouseup', handleMouseUpGlobal);
    };
  }, [isTracking, currentX, startX]);

  // 실시간 드래그 오프셋 계산
  const dragOffset = isTracking ? (currentX - startX) : 0;
  const baseTransform = currentSlide * (slideWidth + 8);
  const finalTransform = baseTransform - dragOffset;

  return (
    <DeviceSwipeContainer>
      <DeviceSlider
        ref={sliderRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateX(-${finalTransform}px)`,
          transition: isTracking ? 'none' : 'transform 0.3s ease',
        }}
      >
        {allSlides.map((item, index) => (
          <DeviceSlide
            key={item.id}
            slideWidth={item.isAddCard ? containerWidth - padding : slideWidth}
          >
            {item.isAddCard ? (
              <AddDeviceCard onClick={handleAddDeviceClick}>
                <AddDeviceIcon>
                  <Icon icon="mdi:plus" fontSize={24} />
                </AddDeviceIcon>
                <Typography variant="body2">
                  {intl.formatMessage({ id: "add_pokoro_device" })}
                </Typography>
              </AddDeviceCard>
            ) : (
              <DeviceCard 
                isSelected={selectedDeviceId === item.id}
                onClick={() => handleDeviceClick(item)}
              >
                <SelectedBadge
                  isSelected={selectedDeviceId === item.id}
                  isDisabled={!item.profile}
                  onClick={(e) => handleSelectedBadgeClick(e, item)}
                >
                  <Icon icon="mdi:check" fontSize={16} />
                </SelectedBadge>
                {fireworkTrigger[item.id] && (
                  <FireworkContainer trigger={!!fireworkTrigger[item.id]} />
                )}
                <Stack className="device-info" gap={0.5} sx={{ width: '100%', position: 'relative'}}>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      display: 'inline-flex',
                      alignSelf: 'flex-start',
                      fontWeight: 700,
                      color: item.profile?.isMemoryDisabled === false ? c.warning : c.grayHardest,
                      backgroundColor: item.profile?.isMemoryDisabled === false
                        ? `${getColor('palette', 'warning')}${Math.round(0.1 * 255).toString(16).padStart(2, '0')}`
                        : `${getColor('grayscale', 'hardest')}${Math.round(0.1 * 255).toString(16).padStart(2, '0')}`,
                      borderRadius: 999,
                      px: 1.25,
                      py: 0.5,
                    }}
                  >
                    {intl.formatMessage({ id: "memory_badge" })} {/* 기억하기 */}
                    {item.profile?.isMemoryDisabled === false ? 'ON' : 'OFF'}
                  </Typography>
                  <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                    <Typography variant="h6" fontWeight={700} noWrap color={c.text01}>
                      {getDeviceDisplayName(item.deviceName)}
                    </Typography>
                    <Icon icon="mdi:chevron-right" fontSize={24} style={{ color: c.text01 }} />
                  </Stack>
                  {!item.profile ? (
                    <>
                      <ProfileSetupButton
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeviceClick(item);
                        }}
                      >
                        {intl.formatMessage({ id: "profile_setup_button" })}
                      </ProfileSetupButton>
                      <ProfileGuideBubble>
                        {intl.formatMessage({ id: "profile_guide_text" })}
                      </ProfileGuideBubble>
                    </>
                  ) : (
                    <Stack direction="row" alignItems="center" gap={0.75} sx={{ minHeight: "28px" }}>
                      <Typography variant="body2" color={c.text01}>
                        {item.profile?.name || intl.formatMessage({ id: "device_detail_gender_unknown" })}
                      </Typography>
                      <Typography variant="caption" color={c.text02}>
                        ({getProfileDescription(item.profile)})
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </DeviceCard>
            )}
          </DeviceSlide>
        ))}
      </DeviceSlider>
      
      {/* {maxSlides > 0 && (
        <PaginationContainer>
          {Array.from({ length: maxSlides + 1 }, (_, index) => (
            <PaginationDot
              key={index}
              active={index === currentSlide}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </PaginationContainer>
      )} */}
      

    </DeviceSwipeContainer>
  );
};

export default injectIntl(
  inject("authStore", "userStore", "modalStore")(observer(PokoroListSlide))
);
